import { useGridFilter } from '@mint-lib/ag-grid/react';
import { useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import {
  CalendarRange,
  DatepickerInput,
  DatepickerInputLabel,
  DatepickerInputRoot,
  type DateRange,
} from '@mint-lib/tw-components/patterns';
import { format } from 'date-fns';

const dateRangeToString = (dateRange?: DateRange) => {
  if (dateRange?.from == null || dateRange?.to == null) {
    return '';
  }
  const from = format(dateRange.from, 'dd/MM/yyyy');
  const to = format(dateRange.to, 'dd/MM/yyyy');
  return `${from} - ${to}`;
};

const doesFilterPass = () => true;

export type DateRangeModel = {
  filterType: 'dateRange';
  startDate: string;
  endDate: string;
};

type GridDateRangeInputProps = {
  model: null | DateRangeModel;
  onModelChange: (model: null | DateRangeModel) => void;
};

export const GridDateRangeInput = (props: GridDateRangeInputProps) => (
  <GridDateRangeInputView
    key={`${props.model?.startDate}-${props.model?.endDate}`}
    {...props}
  />
);

const GridDateRangeInputView = ({
  model,
  onModelChange,
}: GridDateRangeInputProps) => {
  const { t } = useTranslation('@myn/import-hub');

  useGridFilter({
    doesFilterPass,
  });

  const initialRange = {
    from: model?.startDate ? new Date(model.startDate) : undefined,
    to: model?.endDate ? new Date(model.endDate) : undefined,
  };

  const [selectedRange, setSelectedRange] = useState<DateRange>(initialRange);

  const [inputValue, setInputValue] = useState(dateRangeToString(initialRange));

  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  const handleSelect = (dateRange?: DateRange) => {
    setSelectedRange({
      from: dateRange?.from,
      to: dateRange?.to,
    });
  };

  const handleDateApply = () => {
    setIsOpen(false);

    const model =
      selectedRange.from && selectedRange.to
        ? {
            filterType: 'dateRange' as const,
            startDate: format(selectedRange.from, 'yyyy-MM-dd'),
            endDate: format(selectedRange.to, 'yyyy-MM-dd'),
          }
        : null;
    onModelChange(model);
  };

  const handleReset = () => {
    setSelectedRange({
      from: undefined,
      to: undefined,
    });
    onModelChange(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (!e.target.value) {
      handleReset();
    }
  };

  return (
    <div className="py- relative z-1 px-3 py-2">
      <DatepickerInputRoot>
        <DatepickerInputLabel
          label={t('Date range')}
          htmlFor="date-range-input"
        />
        <DatepickerInput
          isPortal
          popoverClassName="z-popover"
          variant="range"
          size="small"
          align="end"
          fullWidth
          value={inputValue}
          open={isOpen}
          onOpenChange={handleOpenChange}
          onChangeInput={handleInputChange}
        >
          <CalendarRange
            defaultMonth={selectedRange.from}
            selected={selectedRange}
            onReset={handleReset}
            onSelect={handleSelect}
            onApply={handleDateApply}
          />
        </DatepickerInput>
      </DatepickerInputRoot>
    </div>
  );
};
